import React from 'react'
import { Eye, Send, Edit, RotateCcw } from 'lucide-react'
import { Timesheet } from '@/types'
import { timesheetUtils } from '@/services/timesheetService'

interface TimesheetTableProps {
  timesheets: Timesheet[]
  loading?: boolean
  onView: (timesheet: Timesheet) => void
  onSubmit?: (timesheet: Timesheet) => void
  onEdit?: (timesheet: Timesheet) => void
  onResubmit?: (timesheet: Timesheet) => void
  onWithdraw?: (timesheet: Timesheet) => void
}

const TimesheetTable: React.FC<TimesheetTableProps> = ({
  timesheets,
  loading = false,
  onView,
  onSubmit,
  onEdit,
  onResubmit,
  onWithdraw
}) => {
  if (loading) {
    return (
      <div className="card">
        <div className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-1/4"></div>
            {[...Array(5)].map((_, index) => (
              <div key={index} className="flex space-x-4">
                <div className="h-4 bg-gray-200 rounded flex-1"></div>
                <div className="h-4 bg-gray-200 rounded w-20"></div>
                <div className="h-4 bg-gray-200 rounded w-16"></div>
                <div className="h-4 bg-gray-200 rounded w-16"></div>
                <div className="h-4 bg-gray-200 rounded w-20"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (timesheets.length === 0) {
    return (
      <div className="card p-8 text-center">
        <div className="text-gray-500 mb-4">
          <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">尚無申報記錄</h3>
        <p className="text-gray-500">開始記錄工時後，您的申報記錄將會顯示在這裡</p>
      </div>
    )
  }

  return (
    <div className="card overflow-hidden">
      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                週次範圍
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                狀態
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                總工時
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                品質分數
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                操作
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {timesheets.map((timesheet) => {
              const weekRange = timesheetUtils.formatWeekRange(timesheet.weekStart, timesheet.weekEnd)
              const statusText = timesheetUtils.getStatusText(timesheet.status)
              const statusColor = timesheetUtils.getStatusColor(timesheet.status)
              const qualityLevel = timesheetUtils.getQualityLevel(timesheet.qualityScore ?? null)
              const workingDays = timesheet.entries ? [...new Set(timesheet.entries.map(entry => 
                entry.startTime.split('T')[0]
              ))].length : 0

              return (
                <tr key={timesheet.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{weekRange}</div>
                    {timesheet.isLateSubmission && (
                      <div className="text-xs text-orange-600 mt-1">🟠 補提交</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusColor}`}>
                      {statusText}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {timesheetUtils.formatHours(timesheet.totalHours)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {workingDays} 工作日
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {timesheet.qualityScore ? (
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {timesheet.qualityScore}分
                        </div>
                        <div className={`text-xs ${qualityLevel.color}`}>
                          {qualityLevel.text}
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">-</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      {timesheet.status === 'DRAFT' && onSubmit && (
                        <button
                          onClick={() => onSubmit(timesheet)}
                          className="text-green-600 hover:text-green-700 transition-colors"
                          title="提交"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      )}
                      {timesheet.status === 'DRAFT' && onEdit && (
                        <button
                          onClick={() => onEdit(timesheet)}
                          className="text-blue-600 hover:text-blue-700 transition-colors"
                          title="編輯"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      )}
                      {timesheet.status === 'REJECTED' && onResubmit && (
                        <button
                          onClick={() => onResubmit(timesheet)}
                          className="text-orange-600 hover:text-orange-700 transition-colors"
                          title="重新提交"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </button>
                      )}
                      {timesheet.status === 'SUBMITTED' && onWithdraw && (
                        <button
                          onClick={() => onWithdraw(timesheet)}
                          className="text-orange-600 hover:text-orange-700 transition-colors"
                          title="撤回"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => onView(timesheet)}
                        className="text-gray-600 hover:text-gray-700 transition-colors"
                        title="查看"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden divide-y divide-gray-200">
        {timesheets.map((timesheet) => {
          const weekRange = timesheetUtils.formatWeekRange(timesheet.weekStart, timesheet.weekEnd)
          const statusText = timesheetUtils.getStatusText(timesheet.status)
          const statusColor = timesheetUtils.getStatusColor(timesheet.status)
          const qualityLevel = timesheetUtils.getQualityLevel(timesheet.qualityScore ?? null)
          const workingDays = timesheet.entries ? [...new Set(timesheet.entries.map(entry => 
            entry.startTime.split('T')[0]
          ))].length : 0

          return (
            <div key={timesheet.id} className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-sm font-medium text-gray-900">📅 {weekRange}</div>
                  {timesheet.isLateSubmission && (
                    <div className="text-xs text-orange-600 mt-1">🟠 補提交</div>
                  )}
                </div>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusColor}`}>
                  {statusText}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <div className="text-xs text-gray-500">總工時</div>
                  <div className="text-sm font-medium text-gray-900">
                    ⏰ {timesheetUtils.formatHours(timesheet.totalHours)} ({workingDays}工作日)
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">品質分數</div>
                  {timesheet.qualityScore ? (
                    <div className="text-sm font-medium text-gray-900">
                      🎯 {timesheet.qualityScore}分 
                      <span className={`text-xs ${qualityLevel.color} ml-1`}>
                        {qualityLevel.text}
                      </span>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">🎯 尚未評分</div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3">
                {timesheet.status === 'DRAFT' && onSubmit && (
                  <button
                    onClick={() => onSubmit(timesheet)}
                    className="text-green-600 hover:text-green-700 transition-colors text-sm"
                  >
                    提交
                  </button>
                )}
                {timesheet.status === 'DRAFT' && onEdit && (
                  <button
                    onClick={() => onEdit(timesheet)}
                    className="text-blue-600 hover:text-blue-700 transition-colors text-sm"
                  >
                    編輯
                  </button>
                )}
                {timesheet.status === 'REJECTED' && onResubmit && (
                  <button
                    onClick={() => onResubmit(timesheet)}
                    className="text-orange-600 hover:text-orange-700 transition-colors text-sm"
                  >
                    重新提交
                  </button>
                )}
                {timesheet.status === 'SUBMITTED' && onWithdraw && (
                  <button
                    onClick={() => onWithdraw(timesheet)}
                    className="text-orange-600 hover:text-orange-700 transition-colors text-sm"
                  >
                    撤回
                  </button>
                )}
                <button
                  onClick={() => onView(timesheet)}
                  className="text-gray-600 hover:text-gray-700 transition-colors text-sm"
                >
                  查看
                </button>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default TimesheetTable
